import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useClient } from 'jsonapi-react';

/**
 * Hook customizado para Infinite Scroll com paginação virtual.
 * Carrega todos os dados da API e aplica paginação virtual no cliente (50 em 50).
 *
 * @param {Object} options - Configurações do hook
 * @param {string} options.endpoint - Endpoint da API (ex: 'users/full')
 * @param {number} [options.pageSize=50] - Quantidade de items por página virtual
 * @param {Object} [options.filters={}] - Objeto com filtros para a API
 * @param {string} [options.searchTerm=''] - Termo de busca local
 * @param {Array<string>} [options.searchFields=[]] - Campos para busca local (ex: ['name', 'email'])
 * @param {number} [options.debounceMs=500] - Delay do debounce em ms
 * @param {Function} [options.onError] - Callback para erros
 * @param {Function} [options.transform] - Função para transformar dados
 * @param {boolean} [options.enabled=true] - Se false, não faz fetch automático
 *
 * @returns {Object} Estado e funções do infinite scroll
 * @returns {Array} data - Array com os dados filtrados e paginados
 * @returns {boolean} loading - Se está carregando dados
 * @returns {boolean} initialLoading - Se é o carregamento inicial
 * @returns {boolean} hasMore - Se há mais páginas virtuais para mostrar
 * @returns {Function} refresh - Função para recarregar tudo do início
 * @returns {Function} setData - Função para atualizar dados manualmente
 * @returns {Function} sentinelRef - Ref para o elemento sentinela do Intersection Observer
 */
export function useInfiniteScroll(options) {
  const { endpoint, pageSize = 50, filters = {}, searchTerm = '', searchFields = ['user.name'], debounceMs = 500, onError, transform = data => data, enabled = true } = options;

  const client = useClient();

  // Estados
  const [allData, setAllData] = useState([]); // Todos os dados da API
  const [virtualPage, setVirtualPage] = useState(1); // Página virtual
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  const [loadingNextPage, setLoadingNextPage] = useState(false); // Carregando próxima página virtual

  // Refs
  const abortControllerRef = useRef(null);
  const isMountedRef = useRef(true);
  const isFetchingRef = useRef(false);
  const isLoadingPageRef = useRef(false);
  const observerRef = useRef(null);
  const prevFiltersJsonRef = useRef('');

  // Refs para valores atuais de hasMore e loading (evita reconexão do observer)
  const hasMoreRef = useRef(false);
  const loadingRef = useRef(false);

  // Estado para controlar quando sentinel está montado
  const [sentinelElement, setSentinelElement] = useState(null);

  // Memoiza os filtros como JSON para detectar mudanças reais
  const filtersJson = useMemo(() => JSON.stringify(filters), [filters]);

  /**
   * Busca valor de campo aninhado (ex: 'user.name')
   * Memoizado para evitar recriação em cada render
   */
  const getNestedValue = useCallback((obj, path) => {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  }, []);

  /**
   * Debounce do searchTerm
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [searchTerm, debounceMs]);

  /**
   * Filtra dados localmente (searchTerm + filtro de status)
   */
  const filteredData = useMemo(() => {
    let result = [...allData];

    // 1. Aplica filtros de API localmente
    if (filters && Object.keys(filters).length > 0) {
      result = result.filter(item => {
        // Verifica cada filtro
        for (const [key, value] of Object.entries(filters)) {
          if (!value || value === 'blank') continue;

          // status
          if (key === 'status' && item.status !== value) {
            return false;
          }

          // instituição
          if (key === 'institution_id' && !item.institution_ids?.includes(value)) {
            return false;
          }

          // curso
          if (key === 'course_id' && !item.course_ids?.includes(value)) {
            return false;
          }

          // turma
          if (key === 'classroom_id' && !item.classroom_ids?.includes(value)) {
            return false;
          }
        }
        return true;
      });
    }

    // 2. Aplica busca por texto
    if (debouncedSearchTerm?.trim()) {
      const searchValue = debouncedSearchTerm.trim().toLowerCase();
      result = result.filter(item =>
        searchFields.some(field => {
          const value = getNestedValue(item, field);
          return value && String(value).toLowerCase().includes(searchValue);
        })
      );
    }

    return result;
  }, [allData, debouncedSearchTerm, searchFields, getNestedValue, filters]);

  /**
   * Busca todos os dados da API
   */
  const fetchAllData = useCallback(async () => {
    if (!enabled || isFetchingRef.current) return;

    isFetchingRef.current = true;
    setLoading(true);

    // Cancela requisição anterior
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    // MUDANÇA: Não usa mais filtros na URL - todos os dados são carregados
    // e filtrados localmente para paginação virtual funcionar corretamente
    const url = endpoint;

    try {
      const response = await client.fetch(url, {
        signal: abortControllerRef.current.signal
      });

      if (response.error) {
        throw new Error(response.error);
      }

      if (!isMountedRef.current) return;

      const transformedData = transform(response.data || []);

      setAllData(transformedData);
    } catch (error) {
      if (error.name !== 'AbortError' && isMountedRef.current) {
        console.error('[useInfiniteScroll] ❌ Erro ao buscar dados:', error);
        onError?.(error);
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
        setInitialLoading(false);
      }
      isFetchingRef.current = false;
    }
  }, [client, enabled, endpoint, filters, onError, transform]);

  /**
   * Dados paginados virtualmente
   */
  const displayedData = useMemo(() => {
    const endIndex = virtualPage * pageSize;
    const slicedData = filteredData.slice(0, endIndex);
    return slicedData;
  }, [filteredData, virtualPage, pageSize]);

  /**
   * Verifica se há mais dados
   */
  const hasMore = useMemo(() => {
    const more = displayedData.length < filteredData.length;
    return more;
  }, [displayedData.length, filteredData.length, allData.length]);

  /**
   * Atualiza refs sempre que hasMore ou loading mudarem
   * Isso permite que o IntersectionObserver use valores atualizados sem recriar o observer
   */
  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  /**
   * Carrega próxima página virtual
   * IMPORTANTE: Usa refs para evitar recriar a função quando hasMore/loading mudam
   * Isso mantém o IntersectionObserver estável
   */
  const loadNextPage = useCallback(() => {
    // Usa refs em vez de valores diretos para não depender de hasMore/loading
    if (isLoadingPageRef.current || !hasMoreRef.current || loadingRef.current) {
      return;
    }

    isLoadingPageRef.current = true;
    setLoadingNextPage(true); // Ativa loader visual

    // Pequeno delay para simular carregamento e mostrar o loader
    setTimeout(() => {
      setVirtualPage(prev => {
        const nextPage = prev + 1;
        isLoadingPageRef.current = false;
        setLoadingNextPage(false); // Desativa loader visual
        return nextPage;
      });
    }, 300); // 300ms de delay para UX suave
  }, []); // Array vazio - função estável que nunca muda

  /**
   * Callback ref para ser notificado quando sentinel é montado
   */
  const sentinelRef = useCallback(node => {
    setSentinelElement(node);
  }, []);

  /**
   * Configura Intersection Observer quando sentinel está disponível
   * IMPORTANTE: Não depende de hasMore/loading para evitar reconexões desnecessárias
   */
  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Limpa observer anterior
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    // Só cria observer se sentinel existe
    if (!sentinelElement) {
      return;
    }

    observerRef.current = new IntersectionObserver(
      entries => {
        const entry = entries[0];

        // Usa refs para valores atualizados sem precisar recriar o observer
        if (entry.isIntersecting && hasMoreRef.current && !loadingRef.current && !isLoadingPageRef.current) {
          loadNextPage();
        }
      },
      {
        root: null,
        rootMargin: '200px',
        threshold: 0
      }
    );

    observerRef.current.observe(sentinelElement);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [enabled, loadNextPage, sentinelElement]); // Removido hasMore e loading das dependências

  /**
   * Refresh - recarrega tudo
   */
  const refresh = useCallback(() => {
    setVirtualPage(1);
    fetchAllData();
  }, [fetchAllData]);

  /**
   * Quando filtros mudam, apenas reseta para página 1 (filtragem é local agora)
   */
  useEffect(() => {
    // Apenas reseta a página virtual quando filtros mudam
    // A filtragem acontece localmente no useMemo de filteredData
    if (!enabled || initialLoading) return;

    if (prevFiltersJsonRef.current !== filtersJson) {
      prevFiltersJsonRef.current = filtersJson;
      setVirtualPage(1);
    }
  }, [filtersJson, enabled, initialLoading]);

  /**
   * Quando searchTerm muda, reseta para página 1
   */
  useEffect(() => {
    setVirtualPage(1);
  }, [debouncedSearchTerm]);

  /**
   * Carregamento inicial - apenas uma vez no mount
   */
  useEffect(() => {
    if (enabled && initialLoading && allData.length === 0) {
      fetchAllData();
    }
    // eslint-disable-next-line
  }, []); // Vazio - roda apenas no mount

  /**
   * Cleanup
   */
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      abortControllerRef.current?.abort();
    };
  }, []);

  return {
    data: displayedData, // Retorna dados paginados virtualmente
    loading,
    initialLoading,
    hasMore,
    loadingNextPage, // Indica se está carregando próxima página virtual
    refresh,
    setData: setAllData,
    sentinelRef
  };
}
